import {
  styled as baseStyled,
  css as baseCss,
  keyframes as baseKeyframes,
  createGlobalStyle as baseCreateGlobalStyle,
  useTheme as baseUseTheme,
  SupportedHTMLElements,
  StyledInstance,
  WebTarget,
} from 'styled-components';

export type ThemedStyled<T extends object> = (<Target extends WebTarget>(
  ...args: Parameters<typeof baseStyled<Target, { theme?: T }>>
) => ReturnType<typeof baseStyled<Target, { theme?: T }>>) & {
  [E in SupportedHTMLElements]: StyledInstance<'web', E, JSX.IntrinsicElements[E] & { theme?: T }>;
};

export type ThemedCss<T extends object> = <Props extends object>(
  ...args: Parameters<typeof baseCss<Props & { theme?: T }>>
) => ReturnType<typeof baseCss<Props & { theme?: T }>>;

export type ThemedKeyframes<T extends object> = <Props extends object>(
  ...args: Parameters<typeof baseKeyframes<Props & { theme?: T }>>
) => ReturnType<typeof baseKeyframes<Props & { theme?: T }>>;

export type ThemedCreateGlobalStyle<T extends object> = <Props extends object>(
  ...args: Parameters<typeof baseCreateGlobalStyle<Props & { theme?: T }>>
) => ReturnType<typeof baseCreateGlobalStyle<Props & { theme?: T }>>;

export const createTheme = <T extends object>(): {
  styled: ThemedStyled<T>;
  css: ThemedCss<T>;
  keyframes: ThemedKeyframes<T>;
  createGlobalStyle: ThemedCreateGlobalStyle<T>;
  useTheme: () => T;
} => {
  const styled = baseStyled as ThemedStyled<T>;
  const css = baseCss as ThemedCss<T>;
  const keyframes = baseKeyframes as ThemedKeyframes<T>;
  const createGlobalStyle = baseCreateGlobalStyle as ThemedCreateGlobalStyle<T>;
  const useTheme = baseUseTheme as () => T;

  return { styled, css, keyframes, createGlobalStyle, useTheme };
};
